import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel';
import HorizontalScroller from '@/components/mobile/HorizontalScroller';
import PlaylistCard from '@/components/playlists/PlaylistCard';
import { fetchUserPlaylists, SPOTIFY_USER_ID, type Playlist } from '@/services/spotifyService';

const HomePlaylistsCarousel: React.FC = () => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { resolvedTheme } = useTheme();
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const data = await fetchUserPlaylists(SPOTIFY_USER_ID, 9);
        if (mounted) setPlaylists(data);
      } catch (e: any) {
        console.error(e);
        if (mounted) setError(e?.message || 'Failed to load playlists');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const textColor = resolvedTheme === 'dark' ? 'text-white' : 'text-black';
  const mutedTextColor = resolvedTheme === 'dark' ? 'text-white/70' : 'text-black/70';

  const jsonLd = useMemo(() => {
    if (!playlists?.length) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      itemListElement: playlists.map((p, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'MusicPlaylist',
          name: p.name,
          url: p.spotifyUrl,
          numTracks: p.songs,
        },
      })),
    };
  }, [playlists]);

  const handleSeeAll = () => navigate('/playlists');

  return (
    <section aria-labelledby="home-playlists" className="py-12">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-8 lg:px-12 xl:px-16">
        <header className="flex items-end justify-between mb-6">
          <div>
            <h2 id="home-playlists" className={cn('text-2xl font-chroma mb-2', textColor)}>Spotify Playlists</h2>
            <p className={cn('text-sm', mutedTextColor)}>Soundtracks for your space. Curated and updated regularly.</p>
          </div>
          <Button variant="link" onClick={handleSeeAll} className={textColor} aria-label="See all playlists">
            See all
          </Button>
        </header>

        {loading && (
          isMobile ? (
            <div className="bleed-right-mobile">
              <HorizontalScroller spacing="custom-10px" noLeftPadding={true} snap snapAlign="start" snapStopAlways={true} showNextCard disableScale>
                 {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="w-[70vw] h-[360px] flex flex-col">
                    <div className="aspect-square rounded-md overflow-hidden flex-none">
                      <Skeleton className="w-full h-full" />
                    </div>
                    <div className="mt-3 space-y-2">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-5/6" />
                    </div>
                  </div>
                ))}
              </HorizontalScroller>
            </div>
          ) : (
            <Carousel opts={{ align: 'start' }}>
              <CarouselContent>
                {Array.from({ length: 9 }).map((_, i) => (
                  <CarouselItem key={i} className="basis-1/3 lg:basis-1/4 xl:basis-1/6">
                    <div className="p-1 h-[360px] flex flex-col">
                      <div className="aspect-square rounded-md overflow-hidden flex-none">
                        <Skeleton className="w-full h-full" />
                      </div>
                      <div className="mt-3 space-y-2">
                        <Skeleton className="h-5 w-3/4" />
                        <Skeleton className="h-4 w-5/6" />
                      </div>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          )
        )}

        {!loading && !error && playlists.length > 0 && (
          isMobile ? (
            <div className="bleed-right-mobile">
                <HorizontalScroller spacing="custom-10px" noLeftPadding={true} snap snapAlign="start" snapStopAlways={true} showNextCard={false} disableScale>
                  {playlists.map((p, index) => (
                    <div key={p.id || p.spotifyUrl} className="w-[60vw]">
                      <PlaylistCard {...p} isPreselected={index === 0} />
                    </div>
                  ))}
              </HorizontalScroller>
            </div>
          ) : (
            <Carousel opts={{ align: 'start' }}>
              <CarouselContent>
                {playlists.map((p) => (
                  <CarouselItem key={p.id || p.spotifyUrl} className="basis-1/3 lg:basis-1/4 xl:basis-1/6">
                    <div className="p-1 h-[360px]">
                      <PlaylistCard {...p} />
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          )
        )}

        {!loading && error && (
          <p className={cn('text-sm', mutedTextColor)}>Could not load playlists right now.</p>
        )}

        {jsonLd && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
        )}
      </div>
    </section>
  );
};

export default HomePlaylistsCarousel;
